import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { ModuleFlow, handleError } from '../../../../shared/utils/error.utils';
import { buildHttpParams } from '../../../../shared/utils/http.utils';
import { ApiResponse } from '../../../../shared/dto/response/api-response.dto';
import { getApiBaseUrl } from '../../../../shared/config/runtime-config';
import {
  EventAvailableRequest,
  EventAvailableResponse,
  EventBraceletColorsResponse,
  EventCancelRequest,
  EventCatalogRequest,
  EventConfigResponse,
  EventCreateRequest,
  EventCreateResponse,
  EventEmptyResponse,
  EventFinalizeRequest,
  EventFindAllRequest,
  EventFindAllResponse,
  EventFindGuestRequest,
  EventFindGuestResponse,
  EventFindGuestsRequest,
  EventFindGuestsResponse,
  EventFindRequest,
  EventFindResponse,
  EventGuestConfigRequest,
  EventMunicipalitiesRequest,
  EventMunicipalitiesResponse,
  EventParishesRequest,
  EventParishesResponse,
  EventRemoveGuestRequest,
  EventRemoveRequest,
  EventRingConfigRequest,
  EventRingsResponse,
  EventStaffAvailableRequest,
  EventStaffAvailableResponse,
  EventStaffConfigRequest,
  EventStatesResponse,
  EventStatusesResponse,
  EventSummaryResponse,
  EventTypesResponse,
  EventUpdateGuestRingRequest,
  EventUpdateRequest,
  EventVenueConfigRequest,
} from '../../domain/dto';
import { EventRepository } from '../../domain/repository';
import {
  EventDetail,
  EventGuestDetail,
  EventGuestPage,
  EventPage,
  EventRingConfig,
  EventSummary,
  EventVenueConfig,
} from '../../domain/model';

interface EventApiPage {
  items: Array<Record<string, unknown>>;
  page: number;
  pageSize: number;
  totalCount: number;
}

@Injectable({ providedIn: 'root' })
export class EventRepositoryImpl implements EventRepository {
  constructor(private http: HttpClient) {}

  findAllPath(): string {
    return this.eventApiUrl;
  }

  findAll(request: EventFindAllRequest): Observable<HttpResponse<EventFindAllResponse>> {
    const params = buildHttpParams(request);

    return this.http
      .get<ApiResponse<EventApiPage>>(this.findAllPath(), { observe: 'response', params })
      .pipe(
        map((response) => {
          const data = response.body?.data;
          const body: EventPage = {
            items: (data?.items ?? []).map((item) => this.mapEventSummary(item)),
            page: this.toNumber(data?.page) ?? 1,
            pageSize: this.toNumber(data?.pageSize) ?? 50,
            totalCount: this.toNumber(data?.totalCount) ?? 0,
          };
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFindAll, error)),
      );
  }

  findById(request: EventFindRequest): Observable<HttpResponse<EventFindResponse>> {
    const { id } = request;
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(`${this.eventApiUrl}/${id}`, { observe: 'response' })
      .pipe(
        map((response) => response.clone({ body: this.mapEventDetail(response.body?.data) })),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  getEventSummaryById(request: EventFindRequest): Observable<HttpResponse<EventSummaryResponse>> {
    return this.findById(request).pipe(
      map((response) => {
        const detail = response.body;
        if (!detail) {
          return response.clone({
            body: {
              id: 0,
              code: '',
              name: '',
              description: '',
              eventType: '',
              eventStatus: '',
              statusCode: '',
              guestCount: 0,
              created: null,
              updated: null,
            },
          });
        }
        const summary: EventSummary = {
          id: detail.id,
          code: detail.code,
          name: detail.name,
          description: detail.description,
          eventType: detail.eventType,
          eventStatus: detail.eventStatus,
          statusCode: detail.statusCode,
          guestCount: 0,
          created: detail.created,
          updated: null,
        };
        return response.clone({ body: summary });
      }),
      catchError((error) => handleError(ModuleFlow.EventFind, error)),
    );
  }

  create(request: EventCreateRequest): Observable<HttpResponse<EventCreateResponse>> {
    return this.http
      .post<ApiResponse<Record<string, unknown>>>(this.eventApiUrl, request, { observe: 'response' })
      .pipe(
        map((response) => {
          const data = this.toRecord(response.body?.data);
          return response.clone({
            body: {
              id: this.toNumber(data['id']) ?? 0,
              code: this.toString(data['code']) ?? '',
            },
          });
        }),
        catchError((error) => handleError(ModuleFlow.EventSave, error)),
      );
  }

  update(request: EventUpdateRequest): Observable<HttpResponse<EventEmptyResponse>> {
    const { id, ...payload } = request;
    return this.http
      .put<ApiResponse<Record<string, never>>>(`${this.eventApiUrl}/${id}`, payload, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: {} })),
        catchError((error) => handleError(ModuleFlow.EventSave, error)),
      );
  }

  remove(request: EventRemoveRequest): Observable<HttpResponse<EventEmptyResponse>> {
    const { id } = request;
    return this.http
      .delete<ApiResponse<Record<string, never>>>(`${this.eventApiUrl}/${id}`, { observe: 'response' })
      .pipe(
        map((response) => response.clone({ body: {} })),
        catchError((error) => handleError(ModuleFlow.EventRemove, error)),
      );
  }

  finalize(request: EventFinalizeRequest): Observable<HttpResponse<EventEmptyResponse>> {
    const { id } = request;
    return this.http
      .patch<ApiResponse<Record<string, never>>>(`${this.eventApiUrl}/${id}/finalize`, {}, { observe: 'response' })
      .pipe(
        map((response) => response.clone({ body: {} })),
        catchError((error) => handleError(ModuleFlow.EventSave, error)),
      );
  }

  cancel(request: EventCancelRequest): Observable<HttpResponse<EventEmptyResponse>> {
    const { id } = request;
    return this.http
      .patch<ApiResponse<Record<string, never>>>(`${this.eventApiUrl}/${id}/cancel`, {}, { observe: 'response' })
      .pipe(
        map((response) => response.clone({ body: {} })),
        catchError((error) => handleError(ModuleFlow.EventSave, error)),
      );
  }

  saveRingConfig(request: EventRingConfigRequest): Observable<HttpResponse<EventConfigResponse>> {
    const { id, ...payload } = request;
    return this.http
      .put<ApiResponse<Record<string, unknown>>>(`${this.eventApiUrl}/${id}/ring-config`, payload, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: this.mapEventDetailFromConfigResponse(response.body?.data) })),
        catchError((error) => handleError(ModuleFlow.EventSave, error)),
      );
  }

  saveVenueConfig(request: EventVenueConfigRequest): Observable<HttpResponse<EventConfigResponse>> {
    const { id, ...payload } = request;
    return this.http
      .put<ApiResponse<Record<string, unknown>>>(`${this.eventApiUrl}/${id}/venue-config`, payload, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: this.mapEventDetailFromConfigResponse(response.body?.data) })),
        catchError((error) => handleError(ModuleFlow.EventSave, error)),
      );
  }

  saveGuestConfig(request: EventGuestConfigRequest): Observable<HttpResponse<EventConfigResponse>> {
    const { id, ...payload } = request;
    return this.http
      .put<ApiResponse<Record<string, unknown>>>(`${this.eventApiUrl}/${id}/guest-config`, payload, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: this.mapEventDetailFromConfigResponse(response.body?.data) })),
        catchError((error) => handleError(ModuleFlow.EventSave, error)),
      );
  }

  updateGuestRing(request: EventUpdateGuestRingRequest): Observable<HttpResponse<EventEmptyResponse>> {
    const { id, idEventGuest, idRing } = request;
    return this.http
      .put<ApiResponse<Record<string, unknown>>>(
        `${this.eventApiUrl}/${id}/guests/${idEventGuest}`,
        { idRing },
        { observe: 'response' },
      )
      .pipe(
        map((response) => response.clone({ body: {} })),
        catchError((error) => handleError(ModuleFlow.EventSave, error)),
      );
  }

  removeGuest(request: EventRemoveGuestRequest): Observable<HttpResponse<EventEmptyResponse>> {
    const { id, idEventGuest } = request;
    return this.http
      .delete<ApiResponse<Record<string, unknown>>>(
        `${this.eventApiUrl}/${id}/guests/${idEventGuest}`,
        { observe: 'response' },
      )
      .pipe(
        map((response) => response.clone({ body: {} })),
        catchError((error) => handleError(ModuleFlow.EventSave, error)),
      );
  }

  findGuestsPath(id: number): string {
    return `${this.eventApiUrl}/${id}/guests`;
  }

  findGuests(request: EventFindGuestsRequest): Observable<HttpResponse<EventFindGuestsResponse>> {
    const { id, ...query } = request;
    const params = buildHttpParams(query);

    return this.http
      .get<ApiResponse<Record<string, unknown>>>(this.findGuestsPath(id), {
        observe: 'response',
        params,
      })
      .pipe(
        map((response) => {
          const data = this.toRecord(response.body?.data);
          const itemsRaw = Array.isArray(data['items']) ? data['items'] : [];
          const body: EventGuestPage = {
            items: itemsRaw.map((item) => {
              const row = this.toRecord(item);
              return {
                id: this.toNumber(row['id']) ?? 0,
                idRing: this.toNumber(row['idRing']) ?? 0,
                idUploadedFile: this.toNumber(row['idUploadedFile']) ?? 0,
                uploadedFileSource: this.toString(row['uploadedFileSource']) ?? '',
                fullIdentification: this.toString(row['fullIdentification']) ?? '',
                idNationalityType: this.toNumber(row['idNationalityType']) ?? 0,
                nationalityTypeCode: this.toString(row['nationalityTypeCode']) ?? '',
                refusedReason: this.toString(row['refusedReason']) ?? null,
                name: this.toString(row['name']) ?? '',
                lastName: this.toString(row['lastName']) ?? '',
                entity: this.toString(row['entity']) ?? '',
                position: this.toString(row['position']) ?? '',
                ringName: this.toString(row['ringName']) ?? '',
                braceletColor: this.toString(row['braceletColor']) ?? '',
                condition: this.toString(row['condition']) ?? '',
                created: this.toString(row['created']) ?? '',
                updated: this.toString(row['updated']) ?? null,
              };
            }),
            page: this.toNumber(data['page']) ?? 1,
            pageSize: this.toNumber(data['pageSize']) ?? 50,
            totalCount: this.toNumber(data['totalCount']) ?? 0,
          };

          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  findGuestById(request: EventFindGuestRequest): Observable<HttpResponse<EventFindGuestResponse>> {
    const { id, idEventGuest } = request;
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(`${this.findGuestsPath(id)}/${idEventGuest}`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const row = this.toRecord(response.body?.data);
          const body: EventGuestDetail = {
            id: this.toNumber(row['id']) ?? 0,
            idEvent: this.toNumber(row['idEvent']) ?? 0,
            idGuest: this.toNumber(row['idGuest']) ?? 0,
            idRing: this.toNumber(row['idRing']) ?? 0,
            idUploadedFile: this.toNumber(row['idUploadedFile']) ?? 0,
            uploadedFileSource: this.toString(row['uploadedFileSource']) ?? '',
            fullIdentification: this.toString(row['fullIdentification']) ?? '',
            idNationalityType: this.toNumber(row['idNationalityType']) ?? 0,
            nationalityTypeName: this.toString(row['nationalityTypeName']) ?? '',
            nationalityTypeCode: this.toString(row['nationalityTypeCode']) ?? '',
            refusedReason: this.toString(row['refusedReason']) ?? null,
            name: this.toString(row['name']) ?? '',
            lastName: this.toString(row['lastName']) ?? '',
            entity: this.toString(row['entity']) ?? '',
            position: this.toString(row['position']) ?? '',
            ringName: this.toString(row['ringName']) ?? '',
            braceletColor: this.toString(row['braceletColor']) ?? '',
            condition: this.toString(row['condition']) ?? '',
            created: this.toString(row['created']) ?? '',
            updated: this.toString(row['updated']) ?? null,
            deleted: this.toString(row['deleted']) ?? null,
            eventCode: this.toString(row['eventCode']) ?? '',
            eventName: this.toString(row['eventName']) ?? '',
            eventType: this.toString(row['eventType']) ?? '',
            eventStatus: this.toString(row['eventStatus']) ?? '',
            statusCode: this.toString(row['statusCode']) ?? '',
          };
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  getStaffAvailable(
    request: EventStaffAvailableRequest,
  ): Observable<HttpResponse<EventStaffAvailableResponse>> {
    const { id } = request;
    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(`${this.eventApiUrl}/${id}/staff-available`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const items = Array.isArray(response.body?.data) ? response.body?.data : [];
          const body = items.map((item) => ({
            id: this.toNumber(item['id']) ?? 0,
            name: this.toString(item['name']) ?? '',
            lastName: this.toString(item['lastName']) ?? '',
            email: this.toString(item['email']) ?? '',
            roleName: this.toString(item['roleName']) ?? '',
          }));
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  getEventsAvailable(request: EventAvailableRequest): Observable<HttpResponse<EventAvailableResponse>> {
    const { id } = request;
    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(`${this.eventApiUrl}/${id}/events-available`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const items = Array.isArray(response.body?.data) ? response.body?.data : [];
          const body = items.map((item) => {
            const summary = this.mapEventSummary(item);
            return {
              ...summary,
              guestCount: this.toNumber(item['guestCount']) ?? 0,
            };
          });
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  saveStaffConfig(request: EventStaffConfigRequest): Observable<HttpResponse<EventConfigResponse>> {
    const { id, ...payload } = request;
    return this.http
      .put<ApiResponse<Record<string, unknown>>>(`${this.eventApiUrl}/${id}/staff-config`, payload, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: this.mapEventDetailFromConfigResponse(response.body?.data) })),
        catchError((error) => handleError(ModuleFlow.EventSave, error)),
      );
  }

  getStates(_request: EventCatalogRequest): Observable<HttpResponse<EventStatesResponse>> {
    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(`${this.catalogApiUrl}/geographics/states`, {
        observe: 'response',
        params: { countryId: environment.COUNTRY_ID },
      })
      .pipe(
        map((response) => {
          const items = Array.isArray(response.body?.data) ? response.body?.data : [];
          const body = items.map((item) => ({
            id: this.toNumber(item['id']) ?? 0,
            code: this.toString(item['code']) ?? '',
            name: this.toString(item['name']) ?? '',
            parentId: this.toNumber(item['parentId']) ?? 0,
            status: this.toNumber(item['status']) ?? 0,
          }));
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  getMunicipalities(
    request: EventMunicipalitiesRequest,
  ): Observable<HttpResponse<EventMunicipalitiesResponse>> {
    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(
        `${this.catalogApiUrl}/geographics/municipalities`,
        {
          observe: 'response',
          params: { stateId: request.stateId },
        },
      )
      .pipe(
        map((response) => {
          const items = Array.isArray(response.body?.data) ? response.body.data : [];
          const body = items.map((item) => this.mapGeographicCatalogItem(item));
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  getParishes(
    request: EventParishesRequest,
  ): Observable<HttpResponse<EventParishesResponse>> {
    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(
        `${this.catalogApiUrl}/geographics/parishes`,
        {
          observe: 'response',
          params: { municipalityId: request.municipalityId },
        },
      )
      .pipe(
        map((response) => {
          const items = Array.isArray(response.body?.data) ? response.body.data : [];
          const body = items.map((item) => this.mapGeographicCatalogItem(item));
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  getEventTypes(_request: EventCatalogRequest): Observable<HttpResponse<EventTypesResponse>> {
    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(`${this.catalogApiUrl}/event-types`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const items = Array.isArray(response.body?.data) ? response.body?.data : [];
          const body = items.map((item) => ({
            id: this.toNumber(item['id']) ?? 0,
            name: this.toString(item['name']) ?? '',
          }));
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  getEventStatuses(_request: EventCatalogRequest): Observable<HttpResponse<EventStatusesResponse>> {
    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(`${this.catalogApiUrl}/event-statuses`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const items = Array.isArray(response.body?.data) ? response.body?.data : [];
          const body = items.map((item) => ({
            id: this.toNumber(item['id']) ?? 0,
            name: this.toString(item['name']) ?? '',
            code: this.toString(item['code']) ?? '',
            description: this.toString(item['description']) ?? '',
            allowedActions: this.toString(item['allowedActions']) ?? '',
          }));
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  getRings(_request: EventCatalogRequest): Observable<HttpResponse<EventRingsResponse>> {
    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(`${this.catalogApiUrl}/rings`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const items = Array.isArray(response.body?.data) ? response.body?.data : [];
          const body = items.map((item) => ({
            id: this.toNumber(item['id']) ?? 0,
            code: this.toString(item['code']) ?? '',
            name: this.toString(item['name']) ?? '',
            description: this.toString(item['description']),
          }));
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  getBraceletColors(
    _request: EventCatalogRequest,
  ): Observable<HttpResponse<EventBraceletColorsResponse>> {
    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(`${this.catalogApiUrl}/bracelet-colors`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const items = Array.isArray(response.body?.data) ? response.body?.data : [];
          const body = items.map((item) => ({
            id: this.toNumber(item['id']) ?? 0,
            name: this.toString(item['name']) ?? '',
            hexCode: this.toString(item['hexCode']),
          }));
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.EventFind, error)),
      );
  }

  private mapEventSummary(item: unknown): EventSummary {
    const row = this.toRecord(item);
    return {
      id: this.toNumber(row['id']) ?? 0,
      code: this.toString(row['code']) ?? '',
      name: this.toString(row['name']) ?? '',
      description: this.toString(row['description']) ?? '',
      eventType: this.toString(row['eventType']) ?? '',
      eventStatus: this.toString(row['eventStatus']) ?? '',
      statusCode: (this.toString(row['statusCode']) ?? '').toUpperCase(),
      guestCount: this.toNumber(row['guestCount']) ?? 0,
      created: this.toString(row['created']),
      updated: this.toString(row['updated']),
    };
  }

  private mapEventDetail(item: unknown): EventDetail {
    const row = this.toRecord(item);
    const venuesRaw = Array.isArray(row['venues']) ? row['venues'] : [];
    const scheduleRaw = Array.isArray(row['schedule']) ? row['schedule'] : [];

    const scheduleByVenue = new Map<
      number,
      Array<{
        id?: number | null;
        idEventVenue?: number | null;
        eventDate: string;
        startTime: string;
        endTime: string;
      }>
    >();

    scheduleRaw.forEach((entry) => {
      const scheduleRecord = this.toRecord(entry);
      const idEventVenue = this.toNumber(scheduleRecord['idEventVenue']) ?? 0;
      if (idEventVenue <= 0) return;

      const eventDate = this.toString(scheduleRecord['eventDate']) ?? '';
      const startTime = this.normalizeTime(this.toString(scheduleRecord['startTime']) ?? '');
      const endTime = this.normalizeTime(this.toString(scheduleRecord['endTime']) ?? '');

      const current = scheduleByVenue.get(idEventVenue) ?? [];
      current.push({
        id: this.toNumber(scheduleRecord['id']),
        idEventVenue: this.toNumber(scheduleRecord['idEventVenue']),
        eventDate,
        startTime,
        endTime,
      });
      scheduleByVenue.set(idEventVenue, current);
    });

    const venues: EventVenueConfig[] = venuesRaw.map((entry) => {
      const venueRecord = this.toRecord(entry);
      const id = this.toNumber(venueRecord['id']) ?? 0;
      return {
        id,
        idState: this.toNumber(venueRecord['idState']) ?? 0,
        stateName: this.toString(venueRecord['stateName']),
        idMunicipality: this.toNumber(venueRecord['idMunicipality']) ?? 0,
        municipalityName: this.toString(venueRecord['municipalityName']),
        idParish: this.toNumber(venueRecord['idParish']) ?? 0,
        parishName: this.toString(venueRecord['parishName']),
        address: this.toString(venueRecord['address']) ?? '',
        name: this.toString(venueRecord['name']) ?? '',
        location: this.toString(venueRecord['location']),
        schedule: scheduleByVenue.get(id) ?? [],
      };
    });

    const ringsRaw = Array.isArray(row['rings']) ? row['rings'] : [];
    const rings: EventRingConfig[] = ringsRaw.map((entry) => {
      const ringRecord = this.toRecord(entry);
      return {
        id: this.toNumber(ringRecord['id']),
        idRing: this.toNumber(ringRecord['idRing']) ?? 0,
        idColor: this.toNumber(ringRecord['idColor']) ?? 0,
        code: this.toString(ringRecord['code']),
        name: this.toString(ringRecord['name']),
        colorName: this.toString(ringRecord['colorName']),
        colorHex: this.toString(ringRecord['colorHex']),
      };
    });

    const uploadedFilesRaw = Array.isArray(row['uploadedFiles']) ? row['uploadedFiles'] : [];
    const uploadedFiles = uploadedFilesRaw.map((entry) => {
      const fileRecord = this.toRecord(entry);
      return {
        id: this.toNumber(fileRecord['id']) ?? 0,
        idEvent: this.toNumber(fileRecord['idEvent']) ?? 0,
        idUploadedFile: this.toNumber(fileRecord['idUploadedFile']) ?? 0,
        entity: this.toString(fileRecord['entity']),
        name: this.toString(fileRecord['name']),
        fileName: this.toString(fileRecord['fileName']),
        created: this.toString(fileRecord['created']),
        guestCount: this.toNumber(fileRecord['guestCount']),
      };
    });

    const staffRaw = Array.isArray(row['staff']) ? row['staff'] : [];
    const staff = staffRaw.map((entry) => {
      const staffRecord = this.toRecord(entry);
      return {
        id: this.toNumber(staffRecord['id']) ?? 0,
        idEvent: this.toNumber(staffRecord['idEvent']) ?? 0,
        idStaff: this.toNumber(staffRecord['idStaff']) ?? 0,
        name: this.toString(staffRecord['name']),
        lastName: this.toString(staffRecord['lastName']),
        roleName: this.toString(staffRecord['roleName']),
      };
    });

    return {
      id: this.toNumber(row['id']) ?? 0,
      code: this.toString(row['code']) ?? '',
      name: this.toString(row['name']) ?? '',
      description: this.toString(row['description']) ?? '',
      idEventType: this.toNumber(row['idEventType']) ?? 0,
      eventType: this.toString(row['eventType']) ?? '',
      eventGuestType: (this.toString(row['eventGuestType']) ?? 'UPLOADED_FILES') as
        | 'UPLOADED_FILES'
        | 'CLONE_EVENT',
      idEventCloned: this.toNumber(row['idEventCloned']),
      clonedEventCode: this.toString(row['clonedEventCode']),
      clonedEventName: this.toString(row['clonedEventName']),
      clonedEventType: this.toString(row['clonedEventType']),
      clonedEventStatus: this.toString(row['clonedEventStatus']),
      clonedStatusCode: this.toString(row['clonedStatusCode']),
      idEventStatus: this.toNumber(row['idEventStatus']) ?? 0,
      eventStatus: this.toString(row['eventStatus']) ?? '',
      statusCode: (this.toString(row['statusCode']) ?? '').toUpperCase(),
      condition: this.toString(row['condition']) ?? '',
      created: this.toString(row['created']) ?? '',
      rings,
      venues,
      uploadedFiles,
      staff,
    };
  }

  private mapEventDetailFromConfigResponse(item: unknown): EventDetail {
    const data = this.toRecord(item);
    const detail = data['detail'] ?? item;
    return this.mapEventDetail(detail);
  }

  private normalizeTime(value: string): string {
    if (!value) return '';
    return value.length >= 5 ? value.slice(0, 5) : value;
  }

  private mapGeographicCatalogItem(item: Record<string, unknown>) {
    return {
      id: this.toNumber(item['id']) ?? 0,
      code: this.toString(item['code']) ?? '',
      name: this.toString(item['name']) ?? '',
      parentId: this.toNumber(item['parentId']) ?? 0,
      status: this.toNumber(item['status']) ?? 0,
    };
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private toNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toString(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const parsed = String(value).trim();
    return parsed.length > 0 ? parsed : null;
  }

  private get eventApiUrl(): string {
    return getApiBaseUrl('events');
  }

  private get catalogApiUrl(): string {
    return getApiBaseUrl('catalogs');
  }
}
